import { withVisibility, WithVisibilityProps } from '../HOC/withVisibility';
import TypingMessage from './TypingMessage';
import styled from 'styled-components';
import EndedMessage from './EndedMessage';
import * as React from 'react';
import { MessageWithMetadata, User } from '../../typedefs/store';
import Message from './Message';
import VideoRequest from './VideoRequest';
import message from '../../reducers/message';

interface Props extends WithVisibilityProps {
    messages: MessageWithMetadata[];
    showAvatars?: boolean;
    typingStatus: { active: boolean; avatar: string };
    convoAvatar: string;
    send: (message: string, tempId?: string) => void;
    scrollToBottom: () => void;
    requestMarkRead: () => void;
    ended: boolean;
    numUnread: number;
    showVideoRequest: boolean;
    webRtcSupported: boolean;
    acceptVideoRequest: () => void;
    agent?: User;
    openLinksExternally: boolean;
}
interface State {}

export class MessageList extends React.Component<Props, State> {
    public static defaultProps: Partial<Props> = {
        showAvatars: false,
    };
    private originalPageTitle: string;
    private titleInterval: any;

    componentDidMount() {
        this.props.scrollToBottom();
        this.props.requestMarkRead();
        this.originalPageTitle = window.document.title;
    }

    componentDidUpdate(prevProps: Props) {
        if (this.props.messages.length !== prevProps.messages.length) {
            this.handleNewMessage();
        } else if (this.props.typingStatus.active && !prevProps.typingStatus.active) {
            this.props.scrollToBottom();
        } else if (this.props.ended !== prevProps.ended) {
            this.props.scrollToBottom();
        } else if (prevProps.showVideoRequest === false && this.props.showVideoRequest === true) {
            this.props.scrollToBottom();
        } else if (
            // scroll to bottom if last message gets marked with an error so the visitor can see the error message
            this.props.messages.length > 0 &&
            this.props.messages[this.props.messages.length - 1].hasSendError !==
            prevProps.messages[this.props.messages.length - 1].hasSendError
        ) {
            this.props.scrollToBottom();
        }

        // once this tab becomes visible, reset page title and mark all read
        if (prevProps.isVisible === false && this.props.isVisible === true) {
            this.props.requestMarkRead();
            clearInterval(this.titleInterval);
            window.document.title = this.originalPageTitle;
        }
    }

    componentWillUnmount() {
        clearInterval(this.titleInterval);
        window.document.title = this.originalPageTitle;
    }

    handleNewMessage() {
        this.props.scrollToBottom();
        if (this.props.isVisible) this.props.requestMarkRead();

        const mostRecentMessage = this.props.messages[this.props.messages.length - 1];
        if (mostRecentMessage.category !== 'message') return;

        this.playSound(mostRecentMessage);
        this.blinkTitleTag();
    }

    playSound(mostRecentMessage: MessageWithMetadata) {
        let soundUrl;
        if (mostRecentMessage.fromType === 'visitor') {
            soundUrl = 'https://s3.amazonaws.com/conversations-files/global/sounds/Peak_Note3.mp3';
        } else {
            soundUrl = 'https://s3.amazonaws.com/conversations-files/global/sounds/Peak_Note1.mp3';
        }
        if (typeof Audio !== 'undefined') {
            try {
                const audio = new Audio(soundUrl);
                audio.play().catch(() => {
                    // ignore
                });
            } catch (e) {
                // ignore
            }
        }
    }

    blinkTitleTag() {
        if (!this.props.isVisible) {
            clearInterval(this.titleInterval);

            let tag = this.props.numUnread ? `${this.props.numUnread} New Message` : 'New Message';
            if (this.props.numUnread > 1) tag += 's';
            const title = `${tag} - ${this.originalPageTitle}`;
            window.document.title = title;
            this.titleInterval = setInterval(() => {
                window.document.title =
                    window.document.title === title ? this.originalPageTitle : title;
            }, 1000);
        }
    }

    render() {
        const { messages, send, showAvatars, ended, openLinksExternally } = this.props;

        return (
            <Wrapper className="conversations-message-list">
                {/* if there are no messages, that means the bot is still loading. show typing indicator */}
                {messages.length === 0 && (
                    <TypingMessage
                        avatar={this.props.convoAvatar}
                        showAvatars={this.props.showAvatars}
                    />
                )}
                {messages.map((message, i) => (
                    <Message
                        key={message.id}
                        message={message}
                        isMostRecentMessage={messages.length - 1 === i}
                        send={send}
                        showAvatars={showAvatars}
                        ended={ended}
                        openLinksExternally={openLinksExternally}
                    />
                ))}
                {this.props.typingStatus.active && (
                    <TypingMessage
                        avatar={this.props.typingStatus.avatar}
                        showAvatars={this.props.showAvatars}
                    />
                )}
                {this.props.ended && <EndedMessage />}
                {this.props.showVideoRequest &&
                    this.props.agent && (
                        <VideoRequest
                            agent={this.props.agent}
                            acceptVideoRequest={this.props.acceptVideoRequest}
                            webRtcSupported={this.props.webRtcSupported}
                        />
                    )}
            </Wrapper>
        );
    }
}

export default withVisibility(MessageList);

const Wrapper = styled.div`
    align-self: flex-end;
    max-height: 100%;
    width: 100%;
`;



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/Shared/MessageList.tsx